import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus, Trash2, KeyRound, ShieldCheck, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });
  const [resetOpen, setResetOpen] = useState(null); // user id
  const [newPassword, setNewPassword] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (e) {
      toast.error("Kullanıcılar çekilemedi", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/users", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
      });
      toast.success("Kullanıcı oluşturuldu");
      setForm({ email: "", password: "", name: "", role: "user" });
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error("Kullanıcı oluşturulamadı", { description: formatApiError(e) });
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Kullanıcı devre dışı" : "Kullanıcı aktif");
      await load();
    } catch (e) {
      toast.error("Güncellenemedi", { description: formatApiError(e) });
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success("Rol güncellendi");
      await load();
    } catch (e) {
      toast.error("Güncellenemedi", { description: formatApiError(e) });
    }
  };

  const doReset = async () => {
    try {
      await api.patch(`/admin/users/${resetOpen}`, { new_password: newPassword });
      toast.success("Parola güncellendi");
      setResetOpen(null);
      setNewPassword("");
    } catch (e) {
      toast.error("Parola değiştirilemedi", { description: formatApiError(e) });
    }
  };

  const doDelete = async (u) => {
    if (!window.confirm(`${u.email} kullanıcısını silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      await load();
    } catch (e) {
      toast.error("Silinemedi", { description: formatApiError(e) });
    }
  };

  return (
    <div>
      <PageHeader
        subtitle="Yönetici erişimi"
        title="Kullanıcı Yönetimi"
        actions={
          <Button
            size="sm"
            className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white h-9"
            onClick={() => setCreateOpen(true)}
            data-testid="create-user-btn"
          >
            <UserPlus className="h-4 w-4 mr-1.5" /> Kullanıcı Ekle
          </Button>
        }
      />

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Kullanıcı
                </th>
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Rol
                </th>
                <th className="px-3 py-2 text-center text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Taslak
                </th>
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Son giriş
                </th>
                <th className="px-3 py-2 text-center text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Aktif
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  İşlem
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="border-b border-zinc-200">
                    <td colSpan={6} className="px-3 py-3">
                      <Skeleton className="h-5 w-full rounded-sm" />
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center text-sm text-zinc-500">
                    Henüz kullanıcı yok.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="row-hover border-b border-zinc-200 bg-white" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-3 text-sm">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-sm bg-zinc-950 text-white flex items-center justify-center text-[10px] font-bold uppercase">
                          {(u.name || u.email).slice(0, 2)}
                        </div>
                        <div>
                          <div className="font-medium">{u.name || "—"}</div>
                          <div className="font-mono text-[11px] text-zinc-500">
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <Select
                        value={u.role}
                        onValueChange={(v) => changeRole(u, v)}
                        disabled={u.id === user.id}
                      >
                        <SelectTrigger
                          className="w-[130px] rounded-sm border-zinc-300 h-8 text-xs"
                          data-testid={`user-role-select-${u.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-sm">
                          <SelectItem value="user">
                            <span className="inline-flex items-center gap-1.5">
                              <UserIcon className="h-3 w-3" /> Kullanıcı
                            </span>
                          </SelectItem>
                          <SelectItem value="admin">
                            <span className="inline-flex items-center gap-1.5 text-[#002FA7]">
                              <ShieldCheck className="h-3 w-3" /> Yönetici
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <Badge className="rounded-sm bg-zinc-100 text-zinc-800 border border-zinc-200 font-mono">
                        {u.draft_count || 0}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-xs font-mono text-zinc-500">
                      {formatDate(u.last_login_at)}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <Switch
                        checked={u.is_active}
                        onCheckedChange={() => toggleActive(u)}
                        disabled={u.id === user.id}
                        data-testid={`user-active-switch-${u.id}`}
                      />
                    </td>
                    <td className="px-3 py-3 text-right space-x-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm border-zinc-300 h-8"
                        onClick={() => {
                          setResetOpen(u.id);
                          setNewPassword("");
                        }}
                        data-testid={`user-reset-btn-${u.id}`}
                      >
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 h-8"
                        onClick={() => doDelete(u)}
                        disabled={u.id === user.id}
                        data-testid={`user-delete-btn-${u.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Yeni kullanıcı</DialogTitle>
            <DialogDescription className="font-mono text-[11px] uppercase tracking-widest">
              Bu kullanıcı hemen giriş yapabilir.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Ad Soyad
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-sm border-zinc-300 mt-1"
                required
                data-testid="create-user-name"
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                E-posta
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-sm border-zinc-300 mt-1"
                required
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Parola (en az 8 karakter)
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-sm border-zinc-300 mt-1"
                required
                minLength={8}
                data-testid="create-user-password"
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Rol
              </Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm border-zinc-300 mt-1" data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value="user">Kullanıcı</SelectItem>
                  <SelectItem value="admin">Yönetici</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setCreateOpen(false)}
              >
                Vazgeç
              </Button>
              <Button
                type="submit"
                className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white"
                disabled={creating}
                data-testid="create-user-submit"
              >
                Oluştur
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetOpen} onOpenChange={(v) => !v && setResetOpen(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Parola sıfırla</DialogTitle>
            <DialogDescription className="font-mono text-[11px] uppercase tracking-widest">
              Kullanıcıya yeni parolayı güvenli iletmeniz gerekir.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
              Yeni parola
            </Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="rounded-sm border-zinc-300 mt-1"
              minLength={8}
              data-testid="reset-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setResetOpen(null)}
            >
              Vazgeç
            </Button>
            <Button
              className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white"
              onClick={doReset}
              disabled={newPassword.length < 8}
              data-testid="reset-password-confirm"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
